import { useState } from 'react'
import { Alert, Button, Drawer, Input, Space, Table, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { PrinterOutlined } from '@ant-design/icons'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { usePermissions } from '@/shared/auth/usePermissions'
import { formatDateTime } from '@/shared/lib/format'
import { CredentialSheet } from './CredentialSheet'
import {
  fetchPoolAccounts,
  fetchPoolCredentials,
  type PoolAccount,
  type PoolCredential,
  type Visp,
} from './networkApi'

const PAGE_SIZE = 50

export function VispAccountsDrawer({ visp, onClose }: { visp: Visp | null; onClose: () => void }) {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [selected, setSelected] = useState<string[]>([])
  const [sheet, setSheet] = useState<PoolCredential[] | null>(null)

  const vispId = visp?.id ?? null
  const accounts = useQuery({
    queryKey: [...queryKeys.network.all, 'pool', vispId, search, page],
    queryFn: () => fetchPoolAccounts(vispId ?? '', { search, page, size: PAGE_SIZE }),
    enabled: vispId !== null,
  })

  const reveal = useMutation({
    mutationFn: (ids: string[]) => fetchPoolCredentials(vispId ?? '', ids),
    onSuccess: setSheet,
  })

  const close = () => {
    setSearch('')
    setPage(0)
    setSelected([])
    setSheet(null)
    reveal.reset()
    onClose()
  }

  const columns: ColumnsType<PoolAccount> = [
    {
      key: 'login',
      title: t('network.visps.credentialLogin'),
      render: (_, row) => <Typography.Text code>{row.login}</Typography.Text>,
    },
    { key: 'createdAt', title: t('network.visps.generatedAt'), render: (_, row) => formatDateTime(row.createdAt) },
  ]

  return (
    <Drawer
      open={visp !== null}
      onClose={close}
      width={640}
      destroyOnHidden
      title={visp ? t('network.visps.accountsTitle', { name: visp.name }) : ''}
      extra={
        can('subscriber.password.set') ? (
          <Button
            icon={<PrinterOutlined />}
            disabled={selected.length === 0}
            loading={reveal.isPending}
            onClick={() => reveal.mutate(selected)}
          >
            {t('network.visps.printSelected', { count: selected.length })}
          </Button>
        ) : null
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {accounts.isError ? <Alert type="error" showIcon message={translateError(accounts.error, t)} /> : null}
        {reveal.isError ? <Alert type="error" showIcon message={translateError(reveal.error, t)} /> : null}
        {accounts.data && accounts.data.total === 0 && !search ? (
          <Alert type="info" showIcon message={t('network.visps.noAvailable')} />
        ) : null}
        <Input.Search
          allowClear
          placeholder={t('network.visps.accountsSearch')}
          onSearch={(value) => {
            setSearch(value.trim())
            setPage(0)
          }}
        />
        <Table<PoolAccount>
          rowKey="id"
          size="small"
          columns={columns}
          dataSource={accounts.data?.rows ?? []}
          loading={accounts.isPending && vispId !== null}
          rowSelection={
            can('subscriber.password.set')
              ? {
                  selectedRowKeys: selected,
                  preserveSelectedRowKeys: true,
                  onChange: (keys) => setSelected(keys.map(String)),
                }
              : undefined
          }
          pagination={{
            current: page + 1,
            pageSize: PAGE_SIZE,
            total: accounts.data?.total ?? 0,
            showSizeChanger: false,
            onChange: (next) => setPage(next - 1),
          }}
        />
      </Space>
      {visp ? (
        <CredentialSheet
          credentials={sheet}
          heading={{ visp: visp.name, center: visp.centerName }}
          onClose={() => setSheet(null)}
        />
      ) : null}
    </Drawer>
  )
}
